import { Icons, type LBIcon } from '@/assets/icons/icons';
import { artifactTypeLabel } from '@/features/artifacts/ArtifactCardShell';
import {
  isCalendarFile,
  isCanvasFile,
  isHtmlFile,
  isImageFile,
  isMarkdownFile,
  isPdfFile,
  isPresentationFile,
  isSpreadsheetFile,
  isWordDocFile,
} from '@/utils/files';

export type SpriteType =
  | 'canvas'
  | 'html'
  | 'image'
  | 'pdf'
  | 'markdown'
  | 'spreadsheet'
  | 'document'
  | 'presentation'
  | 'calendar'
  | 'other';

const SPRITE_TYPE_MATCHERS: [
  SpriteType,
  (contentType: string | undefined, filename: string) => boolean,
][] = [
  ['canvas', isCanvasFile],
  ['image', isImageFile],
  ['html', isHtmlFile],
  ['pdf', isPdfFile],
  ['markdown', isMarkdownFile],
  ['spreadsheet', isSpreadsheetFile],
  ['document', isWordDocFile],
  ['presentation', isPresentationFile],
  ['calendar', isCalendarFile],
];

export const spriteTypeOf = (
  contentType: string | null,
  name: string
): SpriteType =>
  SPRITE_TYPE_MATCHERS.find(([, matches]) =>
    matches(contentType ?? undefined, name)
  )?.[0] ?? 'other';

const SPRITE_TYPE_ICONS: Record<SpriteType, LBIcon> = {
  canvas: Icons.gridPlus,
  image: Icons.imageFile,
  html: Icons.code,
  pdf: Icons.file,
  markdown: Icons.notepad,
  spreadsheet: Icons.table,
  document: Icons.textFile,
  presentation: Icons.presentation,
  calendar: Icons.calendar,
  other: Icons.file,
};

export const spriteIconFor = (
  contentType: string | null,
  name: string
): LBIcon => SPRITE_TYPE_ICONS[spriteTypeOf(contentType, name)];

export const canShareSprite = (sprite: {
  file_id: string | null;
  content_type: string | null;
  name: string;
}): boolean => {
  const type = spriteTypeOf(sprite.content_type, sprite.name);
  return !!sprite.file_id && (type === 'canvas' || type === 'html');
};

export const spriteTypeLabel = (sprite: {
  content_type: string | null;
  name: string;
}): string => artifactTypeLabel(sprite.content_type ?? undefined, sprite.name);

export const spriteSourceLabel = (sprite: {
  source_chat_title: string | null;
}): string | undefined =>
  sprite.source_chat_title ? `From “${sprite.source_chat_title}”` : undefined;

export const spriteSubtitle = (sprite: {
  content_type: string | null;
  name: string;
  source_chat_title: string | null;
}): string => {
  const typeLabel = spriteTypeLabel(sprite);
  const sourceLabel = spriteSourceLabel(sprite);
  return sourceLabel ? `${typeLabel} · ${sourceLabel}` : typeLabel;
};

const SPRITE_TYPE_FILTER_LABELS: [SpriteType, string][] = [
  ['canvas', 'Canvas'],
  ['image', 'Images'],
  ['html', 'HTML'],
  ['pdf', 'PDFs'],
  ['markdown', 'Markdown'],
  ['spreadsheet', 'Spreadsheets'],
  ['document', 'Documents'],
  ['presentation', 'Presentations'],
  ['calendar', 'Calendar Events'],
  ['other', 'Other'],
];

export const SPRITE_TYPE_FILTER_OPTIONS: {
  value: SpriteType;
  label: string;
  Icon: LBIcon;
}[] = SPRITE_TYPE_FILTER_LABELS.map(([value, label]) => ({
  value,
  label,
  Icon: SPRITE_TYPE_ICONS[value],
}));
